














































import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import { AvatarSize } from '@getaccept/lib-ui';
import type { PropType, Ref } from '@vue/composition-api';
import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import debounce from 'debounce';
import type { Recipient } from '../../types/Recipient';
import type { User } from '../../users/types/user';
import { CommentsHelper } from '../helpers/comments.helper';
import type { CommentParticipant } from '../types/comment-participant';
import type { CommentParticipantMetaData } from '../types/comment-participant-meta-data';
import type { CommentReadEvent } from '../types/comment-read-event';
import { CommentStatus } from '../types/comment-status';
import type { CurrentUser } from '../types/current-user';
import type { UpdateCommentDto } from '../types/update-comment-dto';
import CommentInput from './CommentInput.vue';
import MessageContainer from './MessageContainer.vue';

export default defineComponent({
  name: 'ConversationCard',
  components: {
    CommentInput,
    MessageContainer,
  },
  props: {
    comment: {
      type: Object as PropType<Comment>,
      required: true,
    },
    focusOnSelected: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    currentUser: {
      type: Object as PropType<CurrentUser>,
      required: true,
    },
    recipients: {
      type: Array as PropType<Recipient[]>,
      required: true,
    },
    sender: {
      type: Object as PropType<User>,
      required: true,
    },
    readOnly: {
      type: Boolean,
    },
  },
  setup(props, { emit }) {
    const commentInput: Ref<InstanceType<typeof CommentInput> | null> = ref(null);

    const commentCard = ref(null);

    const isTemporary = computed(() => props.comment.id.startsWith(CommentsHelper.TEMP_COMMENT_ID));

    const setFocus = () => {
      if (props.focusOnSelected && props.selected) {
        commentInput.value.setFocus();
      }
    };

    const isDraft = computed(() => props.comment.status === CommentStatus.Draft);

    const isCurrentUserCreator = computed(() => {
      const creatorId = props.comment.createdBy.recipientId || props.comment.createdBy.userId;
      const currentUserId = props.currentUser.recipientId || props.currentUser.userId;
      return creatorId === currentUserId;
    });

    const isResolvable = computed(
      () =>
        props.comment.status === CommentStatus.Active &&
        isCurrentUserCreator.value &&
        !props.readOnly
    );

    const textValue = computed(() => {
      if (isDraft.value) {
        return props.comment.text || '';
      }
      return '';
    });

    const isGhost = computed(
      () =>
        props.comment.editorSelection?.detachedFromContent ||
        props.comment.editorSelection?.detachedFromVersion
    );

    watch(() => props.selected, setFocus);

    const emitText = (text: string) => {
      if (isTemporary.value) {
        return;
      }
      const payload: Partial<UpdateCommentDto> = { text };
      emit('update', payload);
    };

    const handleResolve = () => {
      if (isTemporary.value) {
        return;
      }
      const payload: Partial<UpdateCommentDto> = { status: CommentStatus.Resolved };
      emit('update', payload);
    };

    const handleSubmit = (text: string) => {
      if (isTemporary.value) {
        return;
      }
      if (isDraft.value) {
        emitTextDebounce.clear();
        const payload: Partial<UpdateCommentDto> = { text, status: CommentStatus.Active };
        emit('update', payload);
      } else {
        emit('create-reply', { commentId: props.comment.id, text, mentions: [] });
      }
    };

    const getIsRead = (reads: CommentReadEvent[]) => {
      if (props.currentUser.userId) {
        return CommentsHelper.isReadByUser(reads, props.currentUser.userId);
      }

      if (props.currentUser.recipientId) {
        return CommentsHelper.isReadByRecipient(reads, props.currentUser.recipientId);
      }

      return false;
    };

    const getUserMetaData = (participant: CommentParticipant): CommentParticipantMetaData => {
      if (participant?.recipientId) {
        const recipient = props.recipients.find(({ id }) => id === participant.recipientId);
        return {
          name: recipient?.fullName || '',
          thumbUrl: recipient?.thumbUrl || '',
        };
      }

      if (participant?.userId) {
        return {
          name: (props.sender as User).fullName || '',
          thumbUrl: (props.sender as User).thumbUrl || '',
        };
      }

      return { name: '', thumbUrl: '' };
    };

    const emitTextDebounce = debounce(emitText, 300);

    return {
      AvatarSize,
      commentCard,
      commentInput,
      CommentStatus,
      emitTextDebounce,
      getIsRead,
      getUserMetaData,
      handleResolve,
      handleSubmit,
      isDraft,
      isGhost,
      isResolvable,
      isTemporary,
      textValue,
    };
  },
});
